import React, { useEffect, useState } from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Controller, UseFormSetValue } from "react-hook-form";
import { Box, MenuItem } from "@mui/material";
import { IUserFormInput } from "./ModalUser";
import { IFormProps } from "../../../pages/OfertaPersoanaFizicaPage";
import { IFormJuridicaProps } from "../../../pages/OfertaPersoanaJuridicaPage";
import romanianLocationsData from "../../../assets/romanian_locations_full.json";

export const InputFormAutocomplete = ({
  name1,
  name2,
  control,
  label1,
  label2,
  setValue,
  setValue2,
  setValue3,
}: {
  name1: string;
  name2: string;
  control: any;
  label1: string;
  label2: string;
  setValue?: UseFormSetValue<IUserFormInput>;
  setValue2?: UseFormSetValue<IFormProps>;
  setValue3?: UseFormSetValue<IFormJuridicaProps>;
}) => {
  // States for judete and localitati
  const [judeteRomania, setJudeteRomania] = useState<{ nume: string; auto: string }[]>([]);
  const [localitatiRomania, setLocalitatiRomania] = useState<{ nume: string; simplu?: string }[]>([]);
  const [selectedJudet, setSelectedJudet] = useState<string>("");

  const filterOptions = createFilterOptions({
    matchFrom: "start",
    stringify: (option: string) => option,
  });

  // Process the JSON data on component mount
  useEffect(() => {
    // Extract unique judete
    const judete: { nume: string; auto: string }[] = [];
    const judetSet = new Set<string>();
    
    romanianLocationsData.forEach(location => {
      if (location.name === "Selectati" && !judetSet.has(location.state)) {
        judetSet.add(location.state);
        judete.push({
          nume: location.state,
          auto: location.state.substring(0, 2).toUpperCase() // Creating a simple auto code
        });
      }
    });
    
    setJudeteRomania(judete);
  }, []);

  // Function to handle judet selection
  const handleJudetChange = (judet: string | null) => {
    if (judet) {
      setSelectedJudet(judet);
      
      // Filter localities for the selected judet
      const localities = romanianLocationsData
        .filter(location => location.state === judet && location.name !== "Selectati")
        .map(location => ({
          nume: location.full_name || location.name,
          simplu: location.name
        }));
      
      setLocalitatiRomania(localities);
    } else {
      setSelectedJudet("");
      setLocalitatiRomania([]);
      
      // Reset locality value based on which setValue is available
      if (setValue) {
        setValue("localitateValue", "");
      } else if (setValue2) {
        setValue2("localitate", "");
      } else if (setValue3) {
        setValue3("localitate", "");
      }
    }
  };

  return (
    <Box
      sx={{
        display: {lg: 'flex'},
        position: "relative",
        padding: "0 50px 50px 50px",
        width: setValue2 !== undefined || setValue3 !== undefined ? "25%" : "unset",
      }}
    >
      <Controller
        rules={{ required: true }}
        render={({ field: { onChange, ...props } }) => (
          <Autocomplete
            {...props}
            options={judeteRomania.map(judet => judet.nume)}
            noOptionsText={"Nimic găsit."}
            filterOptions={filterOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                name={name1}
                size={setValue2 !== undefined || setValue3 !== undefined ? "medium" : "small"}
                id={name1}
                label={label1}
                sx={{
                  width: "250px",
                  position: "absolute",
                  left: 0,
                  "& input[type=number]": {
                    "-moz-appearance": "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button": {
                    "-webkit-appearance": "none",
                    margin: 0,
                  },
                  "& input[type=number]::-webkit-inner-spin-button": {
                    "-webkit-appearance": "none",
                    margin: 0,
                  },
                  "& .MuiInputBase-input": {
                    color: "black", // Text color
                    fontFamily: "Catesque", // Font family
                  },
                  "& .MuiInputLabel-root": {
                    color: "black", // Label color
                    fontFamily: "Catesque", // Font family
                    fontSize: "18px",
                    "&.Mui-focused fieldset": {
                      borderColor: "#0054a6", // Focused border color
                    },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#0054a6", // Border color
                    },
                    "&:hover fieldset": {
                      borderColor: "#0054a6", // Hover border color
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#0054a6", // Focused border color
                    },
                  },
                  "& .MuiAutocomplete-endAdornment": {
                    "& .MuiButtonBase-root": {
                      color: "#0054a6",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    },
                  },
                }}
              />
            )}
            onChange={(_, judet) => {
              onChange(judet);
              handleJudetChange(judet);
            }}
            renderOption={(props, option) => (
              <MenuItem
                {...props}
                sx={{ fontFamily: "Catesque", fontSize: "16px" }}
              >
                {option}
              </MenuItem>
            )}
          />
        )}
        defaultValue={""}
        name={name1}
        control={control}
      />
      <Controller
        rules={{ required: true }}
        render={({ field: { onChange, ...props } }) => (
          <Autocomplete
            {...props}
            options={localitatiRomania.map(localitate => localitate.nume)}
            noOptionsText={"Nimic găsit."}
            filterOptions={filterOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                name={name2}
                size={setValue2 !== undefined || setValue3 !== undefined ? "medium" : "small"}
                id={name2}
                label={label2}
                sx={{
                  width: "250px",
                  position: "absolute",
                  right: 0,
                  "& input[type=number]": {
                    "-moz-appearance": "textfield",
                  },
                  "& input[type=number]::-webkit-outer-spin-button": {
                    "-webkit-appearance": "none",
                    margin: 0,
                  },
                  "& input[type=number]::-webkit-inner-spin-button": {
                    "-webkit-appearance": "none",
                    margin: 0,
                  },
                  "& .MuiInputBase-input": {
                    color: "black", // Text color
                    fontFamily: "Catesque", // Font family
                  },
                  "& .MuiInputLabel-root": {
                    color: "black", // Label color
                    fontFamily: "Catesque", // Font family
                    fontSize: "18px",
                    "&.Mui-focused fieldset": {
                      borderColor: "#0054a6", // Focused border color
                    },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#0054a6", // Border color
                    },
                    "&:hover fieldset": {
                      borderColor: "#0054a6", // Hover border color
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#0054a6", // Focused border color
                    },
                  },
                  "& .MuiAutocomplete-endAdornment": {
                    "& .MuiButtonBase-root": {
                      color: "#0054a6",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    },
                  },
                }}
              />
            )}
            onChange={(_, localitate) => {
              onChange(localitate);
            }}
            renderOption={(props, option) => (
              <MenuItem
                {...props}
                sx={{ fontFamily: "Catesque", fontSize: "16px" }}
              >
                {option}
              </MenuItem>
            )}
          />
        )}
        defaultValue={""}
        name={name2}
        control={control}
      />
    </Box>
  );
};